import { useState, FormEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTranslatedList } from "@/hooks/useTranslatedList";

const vacancies = [
  {
    id: 1,
    title: "News Reporter / Journalist",
    department: "News Department",
    location: "Edea, Cameroon",
    requirements: "Degree in Journalism, Mass Communication, or related field. Experience in news reporting preferred.",
    responsibilities: "Report on local, national, and international news. Conduct interviews and write news stories for broadcast.",
    deadline: "2026-09-30",
  },
  {
    id: 2,
    title: "Video Producer / Editor",
    department: "Production",
    location: "Edea, Cameroon",
    requirements: "Experience in video production and editing. Proficiency in editing software such as Adobe Premiere or DaVinci Resolve.",
    responsibilities: "Produce and edit video content for television and digital platforms. Manage post-production workflow.",
    deadline: "2026-09-30",
  },
  {
    id: 3,
    title: "Broadcast Technician",
    department: "Technical & Broadcasting",
    location: "Edea, Cameroon",
    requirements: "Technical knowledge of broadcast equipment, cameras, and studio systems. IT and networking skills are a plus.",
    responsibilities: "Operate and maintain broadcast equipment. Ensure smooth live and recorded broadcasts.",
    deadline: "2026-10-15",
  },
  {
    id: 4,
    title: "Social Media Coordinator",
    department: "Marketing & Digital",
    location: "Edea, Cameroon",
    requirements: "Experience managing social media platforms. Strong content creation and community management skills.",
    responsibilities: "Manage L+TV social media accounts. Create and schedule content. Engage with the online community.",
    deadline: "2026-10-15",
  },
];

export default function CareersPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [selectedJob, setSelectedJob] = useState("");
  const translatedVacancies = useTranslatedList(vacancies, [
    "title",
    "department",
    "location",
    "requirements",
    "responsibilities",
  ]);

  useEffect(() => {
    document.title = "Careers | L+TV TÉLÉVISION";
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    (e.currentTarget as HTMLFormElement).reset();
  };

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <h1 className="text-2xl md:text-3xl font-bold text-white">{t("careers.title")}</h1>
          <p className="text-white/60 text-sm mt-1">{t("careers.subtitle")}</p>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-foreground-700 leading-relaxed text-lg">
            {t("careers.intro")}
          </p>
        </div>

        {/* Vacancies */}
        <h2 className="ltv-section-title">{t("careers.openPositions")}</h2>
        <div className="space-y-4 mb-12">
          {translatedVacancies.map((job) => (
            <div key={job.id} className="bg-white rounded-lg border border-background-200 p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground-900 mb-1">{job.title}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-3">
                    <span className="flex items-center gap-1"><i className="ri-building-line"></i>{job.department}</span>
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{job.location}</span>
                    <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{t("careers.deadline")}: {job.deadline}</span>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="font-medium text-foreground-700">{t("careers.requirements")}:</span> <span className="text-foreground-600">{job.requirements}</span></p>
                    <p className="text-sm"><span className="font-medium text-foreground-700">{t("careers.responsibilities")}:</span> <span className="text-foreground-600">{job.responsibilities}</span></p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedJob(job.title)}
                  className="ltv-btn-accent text-sm flex-shrink-0"
                >
                  {t("careers.applyNow")}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Application Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg border border-background-200 p-6 md:p-8">
            <h2 className="text-lg font-bold text-foreground-900 mb-2">{t("careers.submitTitle")}</h2>
            <p className="text-sm text-foreground-600 mb-6">{t("careers.submitDesc")}</p>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white mx-auto mb-3">
                  <i className="ri-check-line text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-1">{t("careers.submittedTitle")}</h3>
                <p className="text-sm text-green-700">{t("careers.submittedDesc")}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">{t("careers.fullName")} *</label>
                    <input name="name" type="text" className="ltv-input" placeholder={t("careers.fullName")} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">{t("careers.email")} *</label>
                    <input name="email" type="email" className="ltv-input" placeholder="your@email.com" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">{t("careers.phone")} *</label>
                    <input name="phone" type="tel" className="ltv-input" placeholder="+237 ..." required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">{t("careers.position")} *</label>
                    <select
                      name="position"
                      className="ltv-input bg-white"
                      required
                      value={selectedJob}
                      onChange={(e) => setSelectedJob(e.target.value)}
                    >
                      <option value="">{t("careers.selectPosition")}</option>
                      {translatedVacancies.map((j) => (
                        <option key={j.id} value={j.title}>{j.title}</option>
                      ))}
                      <option value="Other">{t("careers.other")}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1">{t("careers.coverLetter")}</label>
                  <textarea name="cover_letter" rows={4} className="ltv-input resize-none" placeholder={t("careers.coverPlaceholder")} maxLength={500}></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1">{t("careers.cvUpload")}</label>
                  <div className="border-2 border-dashed border-background-300 rounded-lg p-6 text-center">
                    <i className="ri-upload-cloud-line text-2xl text-foreground-400 mb-2"></i>
                    <p className="text-sm text-foreground-500">{t("careers.cvDrag")}</p>
                    <p className="text-xs text-foreground-400 mt-1">{t("careers.cvNote")}</p>
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" />
                  </div>
                </div>
                <button type="submit" className="ltv-btn-accent w-full sm:w-auto">
                  {t("careers.submitApp")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}